import { Button, Card, CardContent, CardHeader, CardTitle, FormField, Input } from "@jod/ui";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import type { FormEvent } from "react";
import { useState } from "react";
import {
  changeOwnerPassword,
  getOwnerPrincipal,
  isOwnerAuthenticated,
  ownerMustChangePassword,
  signOutOwner,
} from "../lib/auth-session.ts";

export const Route = createFileRoute("/change-password")({
  beforeLoad: () => {
    if (!isOwnerAuthenticated()) throw redirect({ to: "/login" });
    if (!ownerMustChangePassword()) throw redirect({ to: "/dashboard" });
  },
  component: ChangePasswordScreen,
});

function ChangePasswordScreen() {
  const navigate = useNavigate();
  const principal = getOwnerPrincipal();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New password and confirmation do not match.");
      return;
    }

    setSubmitting(true);
    try {
      await changeOwnerPassword(currentPassword, newPassword);
      void navigate({ to: "/dashboard" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Password change failed.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="font-display text-2xl">Set a new password</CardTitle>
          <p className="text-muted-foreground text-sm">
            {principal?.email
              ? `Required before you can access Owner Admin (${principal.email}).`
              : "Required before you can access Owner Admin."}
          </p>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
            <FormField id="current-password" label="Current password / รหัสผ่านปัจจุบัน">
              <Input
                id="current-password"
                type="password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
              />
            </FormField>
            <FormField id="new-password" label="New password / รหัสผ่านใหม่">
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
            </FormField>
            <FormField id="confirm-password" label="Confirm new password">
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
              />
            </FormField>
            {error ? (
              <p role="alert" className="rounded-md bg-destructive/10 p-3 text-destructive text-sm">
                {error}
              </p>
            ) : null}
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? "Updating…" : "Update password"}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full text-foreground"
              onClick={() => {
                void signOutOwner().then(() => navigate({ to: "/login" }));
              }}
            >
              Sign out
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
